
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SEO } from './SEO';
import {
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon,
  Translate as TranslateIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  WhatsApp as WhatsAppIcon,
  MenuBook,
  Science,
  Language,
  Speed,
  Verified,
  EmojiEvents
} from '@mui/icons-material';
import { AuroraBackground } from './AuroraBackground';

const SarvalokForEducation: React.FC = () => {
  const { t } = useTranslation(['edu', 'seo']);
  
  return (
    <div className="bg-white text-black">
      <SEO
        title={t('seo:edu.title', { defaultValue: 'Educational Solutions | Sarvalok AI for Arts & Humanities Research' })}
        description={t('seo:edu.description', { defaultValue: "Revolutionize research in Arts & Humanities. Provide students, researchers, and educators with direct access to India's vast repository of ancient wisdom and authentic knowledge." })}
        url="/for-education"
      />
      {/* Academic Header */}
      <AuroraBackground className="px-8 lg:px-20 py-16 overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -right-24 z-0 h-80 w-80 rounded-full bg-gradient-to-br from-orange-300/40 via-pink-300/30 to-purple-300/20 blur-3xl dark:from-orange-400/20 dark:via-pink-400/15 dark:to-purple-400/10" />
          <div className="absolute -bottom-28 -left-28 z-0 h-96 w-96 rounded-full bg-gradient-to-tr from-blue-300/40 via-indigo-300/30 to-cyan-300/20 blur-3xl dark:from-blue-400/20 dark:via-indigo-400/15 dark:to-cyan-400/10" />
          <img
            src="/decor/ring3.png"
            alt=""
            className="absolute -top-12 -right-12 z-10 hidden md:block h-auto w-auto max-w-[240px] opacity-90 drop-shadow-2xl"
          />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
            <SchoolIcon className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {t('edu.header_title_line1')}
            <span className="block text-blue-600">{t('edu.header_title_line2')}</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t('edu.header_description')}
          </p>
        </div>
      </AuroraBackground>

      <div className="px-8 lg:px-20 py-16">
        <div className="text-center mb-16">
          <h3 className="text-3xl font-bold text-gray-800 mb-4">{t('edu.transforming_title')}</h3>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-600 mx-auto"></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-green-400 to-blue-500 p-3 rounded-lg">
                <Speed className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('edu.accelerate_research_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('edu.accelerate_research_desc')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-purple-400 to-pink-500 p-3 rounded-lg">
                <MenuBook className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('edu.enhance_curriculum_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('edu.enhance_curriculum_desc')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-blue-400 to-indigo-500 p-3 rounded-lg">
                <Science className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('edu.support_disciplines_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('edu.support_disciplines_desc')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-teal-400 to-cyan-500 p-3 rounded-lg">
                <Language className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('edu.multilingual_scholarship_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('edu.multilingual_scholarship_desc')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-orange-400 to-red-500 p-3 rounded-lg">
                <Verified className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('edu.reliable_knowledge_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('edu.reliable_knowledge_desc')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-3 rounded-lg">
                <LightbulbIcon className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('edu.empower_yoga_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('edu.empower_yoga_desc')}
            </p>
          </div>
        </div>

        {/* Advantage Section */}
        <div className="bg-gradient-to-br from-slate-800 via-blue-900 to-indigo-900 px-8 lg:px-20 py-20 text-white">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold mb-4">{t('edu.advantage_title')}</h3>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="flex items-center mb-4">
                <EmojiEvents className="text-yellow-400 text-2xl mr-3" />
                <h4 className="text-xl font-semibold">{t('edu.unparalleled_accuracy')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('edu.unparalleled_accuracy_desc')}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="flex items-center mb-4">
                <MenuBook className="text-blue-400 text-2xl mr-3" />
                <h4 className="text-xl font-semibold">{t('edu.heritage_ai_insights')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('edu.heritage_ai_insights_desc')}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="flex items-center mb-4">
                <Verified className="text-green-400 text-2xl mr-3" />
                <h4 className="text-xl font-semibold">{t('edu.direct_source_exploration')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('edu.direct_source_exploration_desc')}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="flex items-center mb-4">
                <Speed className="text-purple-400 text-2xl mr-3" />
                <h4 className="text-xl font-semibold">{t('edu.efficiency_boost')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('edu.efficiency_boost_desc')}
              </p>
            </div>
          </div>
        </div>

        {/* Institution Section */}
        <div className="bg-white px-8 lg:px-20 py-20">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">{t('edu.unique_academic_resource')}</h3>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {t('edu.bring_to_institution_desc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h4 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">{t('edu.contact_information')}</h4>
              <div className="space-y-3">
                <p><span className="font-semibold">{t('edu.email_for_institutions')}</span> <a href="mailto:Enterprise@sarvalok.com" className="text-blue-600 underline">Enterprise@sarvalok.com</a></p>
                <p><span className="font-semibold">{t('edu.general_support')}</span> <a href="mailto:support@sarvalok.com" className="text-blue-600 underline">support@sarvalok.com</a></p>
                <p><span className="font-semibold">{t('edu.phone')}</span> +91 8125831103</p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h4 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">{t('edu.quick_connect')}</h4>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {t('edu.bring_to_institution')}
              </p>
              <a href="mailto:Enterprise@sarvalok.com" className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition">
                {t('edu.contact_information')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SarvalokForEducation;
